import { GetGroupUsersDto, GetUsersDto, PatchUserBodyDto } from "@/lib/gen-api";
import {
  groupUserLinkControllerLinkUser,
  groupUserLinkControllerUnlinkUser,
  groupUserControllerCreateUser,
  groupUserControllerDeleteUser,
  groupUserControllerGetUsers,
  groupUserControllerUpdateUser,
  userControllerDeleteUser,
  userControllerGetUser,
  userControllerGetUsers,
  userControllerUpdateUser,
} from "@/lib/gen-api/sdk.gen";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useIsApiReady } from "../hooks/use-api-ready";
import { queryKeys } from "../lib/query-keys";

export type SystemRole = GetUsersDto["data"][number]["role"];
export type GroupUser = GetGroupUsersDto["data"][number];

export const useUsersData = (groupUuid: string | null) => {
  const queryClient = useQueryClient();

  const {
    data: users,
    isPending,
    error,
  } = useQuery({
    enabled: useIsApiReady() && !!groupUuid,
    queryKey: queryKeys.groupUsers(groupUuid!),
    queryFn: () =>
      groupUserControllerGetUsers({
        path: {
          groupUuid: groupUuid!,
        },
      }).then((res) => (res.data?.data ?? []) as GroupUser[]),
  });

  const { mutateAsync: createUser } = useMutation({
    mutationFn: (userData: {
      email: string;
      role: "admin" | "creator" | "member";
    }) =>
      groupUserControllerCreateUser({
        path: {
          groupUuid: groupUuid!,
        },
        body: userData,
      }),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.groupUsers(groupUuid!),
      });
    },
  });

  const { mutateAsync: updateUserRole } = useMutation({
    mutationFn: (vars: {
      userUuid: string;
      role: "admin" | "creator" | "member";
    }) =>
      groupUserControllerUpdateUser({
        path: {
          groupUuid: groupUuid!,
          userUuid: vars.userUuid,
        },
        body: { role: vars.role },
      }),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.groupUsers(groupUuid!),
      });
    },
  });

  const { mutateAsync: deleteUser } = useMutation({
    mutationFn: (userUuid: string) =>
      groupUserControllerDeleteUser({
        path: {
          groupUuid: groupUuid!,
          userUuid: userUuid,
        },
      }),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.groupUsers(groupUuid!),
      });
    },
  });

  const { mutateAsync: linkUser } = useMutation({
    mutationFn: (userUuid: string) =>
      groupUserLinkControllerLinkUser({
        path: {
          groupUuid: groupUuid!,
          userUuid: userUuid,
        },
      }),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.groupUsers(groupUuid!),
      });
    },
  });

  const { mutateAsync: unlinkUser } = useMutation({
    mutationFn: (userUuid: string) =>
      groupUserLinkControllerUnlinkUser({
        path: {
          groupUuid: groupUuid!,
          userUuid: userUuid,
        },
      }),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.groupUsers(groupUuid!),
      });
    },
  });

  const getUserForUserUuid = (userUuid: string | null) => {
    if (!users) {
      return undefined;
    }
    const user = users.find((user) => user.uuid === userUuid);
    return user;
  };

  return {
    users: users ?? [],
    isLoading: isPending,
    error,
    getUserForUserUuid,
    createUser,
    updateUserRole,
    deleteUser,
    linkUser,
    unlinkUser,
  };
};

export const useGlobalUsersData = (enabled = true) => {
  const {
    data: users,
    isPending,
    error,
  } = useQuery({
    enabled: useIsApiReady() && enabled,
    queryKey: queryKeys.users(),
    queryFn: () => userControllerGetUsers().then((res) => res.data?.data ?? []),
  });

  return {
    users: users ?? [],
    isLoading: isPending,
    error,
  };
};

export const useGlobalUserData = (userUuid: string | undefined) => {
  const queryClient = useQueryClient();

  const {
    data: user,
    isPending,
    error,
  } = useQuery({
    enabled: useIsApiReady() && !!userUuid,
    queryKey: queryKeys.user(userUuid!),
    queryFn: () =>
      userControllerGetUser({ path: { userUuid: userUuid! } }).then(
        (res) => res.data ?? null,
      ),
  });

  const { mutateAsync: updateUser, isPending: isUpdatingUser } = useMutation({
    mutationFn: (vars: { body: PatchUserBodyDto; userUuid?: string }) =>
      userControllerUpdateUser({
        path: {
          userUuid: vars.userUuid || userUuid!,
        },
        body: vars.body,
      }),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.user(userUuid!),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.users(),
      });
    },
  });

  const { mutateAsync: deleteUser } = useMutation({
    mutationFn: async (userUuid: string) => {
      const response = await userControllerDeleteUser({
        path: { userUuid: userUuid! },
      });
      if (response.error) {
        throw new Error("Failed to delete user");
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.user(userUuid!),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.users(),
      });
    },
  });

  return {
    user,
    isLoading: isPending || isUpdatingUser,
    error,
    updateUser,
    deleteUser,
  };
};
